import { DateRange, TimeSpan, UTC, utc, getTimezoneOffset } from 'rewire-common';
import { ITimeFormat } from '../DataTypes';

export type DatePart =
  | 'year'
  | 'year:4'
  | 'year:2'
  | 'month'
  | 'month:2'
  | 'month:short'
  | 'month:long'
  | 'month:narrow'
  | 'month:numeric'
  | 'day'
  | 'day:2'
  | 'day:numeric'
  | 'weekday'
  | 'weekday:short'
  | 'weekday:long'
  | 'weekday:narrow';
// returns a function that does date formatting given a templated string. will only accept template parameters that include the date parts type above so it is typescript/intellisense friendly.
// i.e. dateFormatter`${'year:4'}-${'month:2'}-${'day:2'}`
export function dateFormatter(strings: any, ...keys: DatePart[]) {
  const _format: any = { timeZone: 'UTC' };
  const _parts: any = [];

  for (const key of keys) {
    switch (key) {
      case 'weekday':
      case 'weekday:long':
        _format.weekday = 'long';
        _parts.push('weekday');
        break;

      case 'weekday:short':
        _format.weekday = 'short';
        _parts.push('weekday');
        break;

      case 'weekday:narrow':
        _format.weekday = 'narrow';
        _parts.push('weekday');
        break;

      case 'year':
      case 'year:4':
        _format.year = 'numeric';
        _parts.push('year');
        break;
      case 'year:2':
        _format.year = '2-digit';
        _parts.push('year');
        break;

      case 'month':
      case 'month:2':
        _format.month = '2-digit';
        _parts.push('month');
        break;

      case 'month:numeric':
        _format.month = 'numeric';
        _parts.push('month');
        break;

      case 'month:short':
        _format.month = 'short';
        _parts.push('month');
        break;

      case 'month:long':
        _format.month = 'long';
        _parts.push('month');
        break;

      case 'month:narrow':
        _format.month = 'narrow';
        _parts.push('month');
        break;

      case 'day':
      case 'day:2':
        _format.day = '2-digit';
        _parts.push('day');
        break;

      case 'day:numeric':
        _format.day = 'numeric';
        _parts.push('day');
        break;
    }
  }

  const dateTimeFormat = new Intl.DateTimeFormat('en-US', _format);

  return (date?: UTC): string => {
    const utc = date?.utc;
    if (!utc) return '';
    if (UTC.MaxValue.utc === utc) return '';
    if (UTC.MinValue.utc === utc) return '';

    const parts = dateTimeFormat.formatToParts(utc).reduce(
      (prev, curr) => {
        prev[curr.type] = curr.value;
        return prev;
      },
      {} as Record<string, string>,
    );
    let result = '';
    for (let index = 0; index < strings.length; index++) {
      result += strings[index];
      if (index < _parts.length) result += parts[_parts[index]];
    }
    return result;
  };
}

export const YMDFormat = dateFormatter`${'year:4'}-${'month:2'}-${'day:2'}`;
export function stringToYMD(date: string): string {
  return YMDFormat(utc(date));
}

export const makeFriendlyDate = dateFormatter`${'weekday:long'}, ${'year:4'}-${'month:2'}-${'day:2'}`;
export const makeShortCellDate = dateFormatter`${'weekday:short'} ${'month:2'}/${'day:2'}`;

export function isSame(range1?: DateRange, range2?: DateRange) {
  if (!range1 && !range2) return true;
  if (!range1 || !range2) return false;
  return range1.equals(range2);
}

export function pluckStartEnd(effective: DateRange): string {
  if (effective.start.equals(effective.end)) return effective.start.toDateString();
  return `${effective.start.toDateString()} - ${effective.end.toDateString()}`;
}

const MMMDDYYYY = dateFormatter`${'month:short'} ${'day:2'}, ${'year:4'}`;
const MMMDD = dateFormatter`${'month:short'} ${'day:2'}`;
const DDYYYY = dateFormatter`${'day:2'}, ${'year:4'}`;

export function makeFriendlyRange(dateRange: DateRange): string {
  if (!dateRange?.length) return '';

  if (dateRange.length === 1) {
    return MMMDDYYYY(dateRange.start);
  }

  if (dateRange.start.year !== dateRange.end.year) {
    return `${MMMDDYYYY(dateRange.start)} to ${MMMDDYYYY(dateRange.end)}`;
  }

  if (dateRange.start.month === dateRange.end.month) {
    return `${MMMDD(dateRange.start)} to ${DDYYYY(dateRange.end)}`;
    // return `${startDate.format('MMM')} ${startDate.format('D')} to ${endDate.format('D')}, ${startDate.year()}`;
  }
  return `${MMMDD(dateRange.start)} to ${MMMDDYYYY(dateRange.end)}`;
}

export function formatTimeNoRollover(v?: any, earnedDate?: UTC): string {
  if (!(v instanceof UTC)) {
    return v;
  }

  if (!earnedDate) {
    return formatTime(v);
  }

  const hoursOffset = v.clone().startOfDay().subtractDate(earnedDate.clone().startOfDay(), TimeSpan.hours);
  const d = new Date(v.utc);
  const hours = d.getUTCHours() + hoursOffset;
  const minutes = d.getUTCMinutes();
  return `${v.pad(hours)}:${v.pad(minutes)}`;
}

// ===========[ Time Formatting functions ]===================

const militaryClock: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'GMT',
  hourCycle: 'h23',
};

const militaryFormatter = new Intl.DateTimeFormat('en-US', militaryClock);

const ampmClock: Intl.DateTimeFormatOptions = {
  hour: 'numeric', // 'numeric' => 3:15 PM '2-digit' => 03:15 PM
  minute: 'numeric',
  timeZone: 'GMT',
  hourCycle: 'h11',
  hour12: true,
};
const uppercaseFormatter = new Intl.DateTimeFormat('en-US', ampmClock);
const lowerCaseFormatter = new Intl.DateTimeFormat('en-CA', ampmClock);

export interface TimeFormat {
  id: ITimeFormat;
  name: string;
}

export function getTimeFormats(): TimeFormat[] {
  return [
    { id: 'military', name: 'Military (e.g. 14:23)' },
    { id: 'uppercaseAMPM', name: 'Uppercase AM/PM (e.g. 2:23 PM)' },
    { id: 'lowercaseAMPM', name: 'Lowercase a.m./p.m. (e.g. 2:23 p.m.)' },
  ];
}

let _timeFormat: TimeFormat | undefined = getTimeFormats()[0]; // default

export function setTimeFormat(format?: TimeFormat) {
  _timeFormat = format;
}

export function getTimeFormat(id: any): TimeFormat | undefined {
  if (typeof id === 'string') {
    const timeFormat: TimeFormat | undefined = getTimeFormats().find((tf) => tf.id === id);
    return timeFormat;
  }
  return undefined;
}

export function formatTime(dt: any, format?: TimeFormat): string {
  if (!dt) return dt;

  let utc = new UTC(dt);
  if (!utc.isValid) return dt;
  utc = utc.roundToMinutes();
  if (!format) format = _timeFormat;
  switch (format?.id) {
    case 'military':
      return militaryFormatter.format(utc.toDate());
    case 'uppercaseAMPM':
      return uppercaseFormatter.format(utc.toDate());
    case 'lowercaseAMPM':
      return lowerCaseFormatter.format(utc.toDate());
    default:
      return utc.toTimeString();
  }
}

export function localTimeZoneUTCNow(): UTC {
  return UTC.nowtz(Intl.DateTimeFormat().resolvedOptions().timeZone);
}

export function utcDateToLocalDate(date: Date) {
  return new Date(date.getTime() - getTimezoneOffset(date));
}

export function getMinMaxDates(dates: UTC[]) {
  let minDate = dates[0];
  let maxDate = dates[0];
  for (let i = 1; i < dates.length; i++) {
    if (minDate > dates[i]) {
      minDate = dates[i];
    } else if (maxDate < dates[i]) {
      maxDate = dates[i];
    }
  }

  return { minDate, maxDate };
}
